import React, { createContext, useCallback, useContext, useState } from 'react';

import { Benefit, KAEUFER_PLUS_BENEFITS } from '../../../@types/benefits';
import { useSelectBenefit, useUserInteractions } from '../../../customHooks/userInteractions';
import useNavigationPaths from '../../../customHooks/useNavigationPaths.hook';

export interface BenefitsContextState {
  benefit: Benefit | null;
  onSelectBenefit: (type: Benefit) => void;
  onCloseModal: () => void;
}

export const BenefitsContext = createContext({} as BenefitsContextState);

type BenefitsProviderProps = {
  children: React.ReactNode;
};

export const BenefitsProvider = ({ children }: BenefitsProviderProps) => {
  const [benefit, setBenefit] = useState<Benefit | null>(null);
  const { clearNestedPath } = useNavigationPaths();
  const { viewedBenefits } = useUserInteractions();
  const { mutate: select } = useSelectBenefit();

  const onSelectBenefit = useCallback(
    async (benefit: Benefit) => {
      setBenefit(benefit);
      // this logic needed only for kaeuferplus benefits
      if (KAEUFER_PLUS_BENEFITS.includes(benefit) && !viewedBenefits?.includes(benefit)) {
        select(benefit);
      }
    },
    [viewedBenefits, select],
  );

  const onCloseModal = () => {
    setBenefit(null);
    clearNestedPath();
  };

  return (
    <BenefitsContext.Provider
      value={{
        benefit,
        onSelectBenefit,
        onCloseModal,
      }}
    >
      {children}
    </BenefitsContext.Provider>
  );
};

export const useBenefits = () => {
  const context = useContext(BenefitsContext);
  if (Object.keys(context)?.length === 0) {
    throw new Error('useBenefits must be used within a BenefitsProvider');
  }
  return context;
};
